import { Box, Typography, Stack, Grid, useTheme } from '@mui/material';
import React, { useEffect, useState } from 'react';

interface MarketingPlatformCardProps {
  marketingPlatformData: {
    backgroundImage: string[];
    title: string;
    description: string | JSX.Element;
    icon: string;
    badges: { id: number; icon: string; text: string }[];
  };
}

const MarketingPlatformCard: React.FC<MarketingPlatformCardProps> = ({ marketingPlatformData }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const theme = useTheme();

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex(prevIndex => (prevIndex + 1) % marketingPlatformData.backgroundImage.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [marketingPlatformData.backgroundImage.length]);

  return (
    <Box
      sx={{
        width: '100%',
        position: 'relative',
        borderRadius: '10px',
        overflow: 'hidden'
      }}
    >
      {/* Image */}
      <Box
        sx={{
          background: 'linear-gradient(139.56deg, #0CAECF -29.1%, #580F6B 96.45%)',
          p: '2px',
          borderRadius: '8px',
          display: 'inline-block',
          m: '0px 16px',
          position: 'relative',
          overflow: 'hidden',
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '16%',
            background: 'linear-gradient(to top, rgba(0, 0, 0, 0.6), transparent)',
            borderRadius: '0 0 8px 8px',
            pointerEvents: 'none'
          },
          [theme.breakpoints.down('md')]: {
            maxHeight: '168px'
          }
        }}
      >
        <Box
          sx={{
            width: '100%',
            height: { xs: '240px', sm: '300px', md: '598px' },
            position: 'relative',
            borderRadius: '6px',
            overflow: 'hidden'
          }}
        >
          <Box
            component="img"
            src={marketingPlatformData.backgroundImage[currentImageIndex]}
            alt="Marketing Background"
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block'
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(to  right, rgba(0, 0, 0, 0.95) 13%, transparent 100%)',
              pointerEvents: 'none',
              [theme.breakpoints.down('md')]: {
                background: 'linear-gradient(to top, rgba(0, 0, 0, 0.95) 40%, transparent 100%)'
              }
            }}
          />
        </Box>
      </Box>

      {/* Desktop: Content Overlay on the Image */}
      <Box
        sx={{
          position: 'absolute',
          top: { xs: -113, lg: 50, md: 100 },
          height: '60%',
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 2,
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          alignItems: 'start',
          justifyContent: 'center',
          textAlign: 'center',
          p: { xs: 3, sm: 4, md: 6 },
          color: 'white',
          gap: '18px'
        }}
      >
        <Box sx={{ mb: 2 }}>
          <Box
            component="img"
            src={marketingPlatformData.icon}
            alt="icon"
            sx={{
              objectFit: 'cover',
              width: '10px',
              height: '10px',
              [theme.breakpoints.up('md')]: {
                width: '50px',
                height: '50px'
              }
            }}
          />
        </Box>

        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            fontSize: { xs: '22px', sm: '24px', md: '29.03px' },
            lineHeight: '120%',
            fontFamily: 'Inter',

            width: { xs: '100%', sm: '80%', md: '60%', lg: '45%' },
            alignSelf: 'flex-start',
            textAlign: 'left'
          }}
        >
          {marketingPlatformData.title}
        </Typography>

        {/* Description */}
        <Typography
          sx={{
            fontWeight: 300,
            fontSize: { xs: '14px', sm: '15.5px', md: '16.8px' },
            lineHeight: '125%',
            fontFamily: 'Inter',
            textAlign: 'left',
            color: '#C0C0C0',
            marginTop: 2,
            width: { xs: '100%', sm: '80%', md: '50%', lg: '28%' },
            alignSelf: 'flex-start'
          }}
        >
          {marketingPlatformData.description}
        </Typography>

        {/* Badges */}
        <Stack
          direction="row"
          spacing={2.5}
          flexWrap="wrap"
          justifyContent="center"
          sx={{
            mt: { xs: 1, md: 1, lg: 2, fontSize: '18px', fontWeight: 400 },
          }}
        >
          {marketingPlatformData.badges.map(badge => (
            <Badge key={badge.id} icon={badge.icon} text={badge.text} />
          ))}
        </Stack>
      </Box>

      {/* Mobile: Content at the Bottom of the Image */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 2,
          top: -65,
          display: { xs: 'flex', md: 'none' },
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          p: { xs: 3, sm: 4 },
          color: 'white',
          [theme.breakpoints.down('md')]: {
            top: -132
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box
            sx={{
              backgroundColor: '#000',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '45px',
              height: '35px'
            }}
          >
            <Box component="img" src={marketingPlatformData.icon} alt="icon" sx={{ objectFit: 'cover' }} width={20} height={20} />
          </Box>
          <Typography
            sx={{
              fontWeight: 200,
              fontSize: { xs: '15px', sm: '15px' },
              lineHeight: '110%',
              fontFamily: 'Inter',
              ml: 2,
              textAlign: 'left'
            }}
          >
            {marketingPlatformData.title}
          </Typography>
        </Box>
      </Box>

      {/* Mobile: Description and Badges Below the Image */}
      <Box
        sx={{
          p: { xs: 3, sm: 4, md: 6 },
          backgroundColor: '#151515',
          display: { xs: 'flex', sm: 'flex', md: 'none' }, // Show on mobile
          flexDirection: 'column',
          gap: 3,
          visibility: { xs: 'visible', md: 'hidden' },
          [theme.breakpoints.down('md')]: {
            gap: 2,
            mb: 0
          }
        }}
      >
        <Grid container spacing={2} columns={3}>
          {marketingPlatformData.badges.map(badge => (
            <Grid item xs={1} key={badge.id}>
              <Badge icon={badge.icon} text={badge.text} />
            </Grid>
          ))}
        </Grid>
        <Typography
          sx={{
            fontWeight: 300,
            fontSize: { xs: '14px', sm: '15.5px', md: '16.8px' },
            lineHeight: '120%',
            fontFamily: 'Inter',
            textAlign: 'left',
            color: '#C0C0C0'
          }}
        >
          {marketingPlatformData.description}
        </Typography>
      </Box>
    </Box>
  );
};

interface BadgeProps {
  icon: string;
  text: string;
}

const Badge: React.FC<BadgeProps> = ({ icon, text }) => {
  return (
    <Box
      sx={{
        backgroundColor: '#272727',
        px: 2,
        height: '30px',
        borderRadius: '8px',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1.5,
        color: '#C0C0C0',
        fontFamily: 'Inter',
        fontSize: '11px',
        whiteSpace: 'nowrap'
      }}
    >
      <img src={icon} alt={text} style={{ width: 13, height: 13 }} />
      <span>{text}</span>
    </Box>
  );
};

export default MarketingPlatformCard;
